// src/utils/api.ts

import { AppMessage, MessageSetter, showTempMessage } from './messages';

export interface ApiRequestOptions {
  /** Whether to automatically include Authorization header from localStorage authToken.
   * Defaults to true for security - set to false only for truly public endpoints.
   */
  useAuth?: boolean;
  /** Whether to bypass workspace header (for portal admin routes) */
  bypassWorkspace?: boolean;
  /** Optional message setter for unified error/success handling */
  setMessage?: MessageSetter;
  /** Optional success toast text */
  successMessage?: string;
  /** Auto-clear duration for success message */
  successTimeoutMs?: number;
}

/**
 * Wrapper around fetch with:
 * - auth header (defaults to true for security)
 * - JSON parsing
 * - unified error handling
 */
export async function apiRequest<T = any>(
  input: RequestInfo,
  init: RequestInit = {},
  options: ApiRequestOptions = {}
): Promise<T> {
  // Default to true for security - require explicit opt-out for public endpoints
  const { useAuth = true, setMessage, successMessage, successTimeoutMs = 3000 } = options;

  const headers: HeadersInit = {
    ...(init.headers || {}),
  };

  // Only set Content-Type for non-GET if body is present and header not provided
  const method = (init.method || 'GET').toUpperCase();
  if (method !== 'GET' && !('Content-Type' in headers)) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  if (useAuth) {
    const token = localStorage.getItem('authToken');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      
      // Try to get workspace ID from localStorage first, but validate against token
      let workspaceId: string | null = localStorage.getItem('currentWorkspaceId');
      
      // Always validate workspace against token to prevent cross-user workspace access
      if (!options.bypassWorkspace) {
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const tokenWorkspaceId = tokenPayload.workspaceId;
          
          // If token has a workspaceId, use it (it's the source of truth)
          if (tokenWorkspaceId && typeof tokenWorkspaceId === 'string') {
            // If localStorage has a different workspace, clear it and use token's workspace
            if (workspaceId && workspaceId !== tokenWorkspaceId) {
              console.warn('Workspace mismatch: localStorage workspace does not match token workspace. Clearing localStorage workspace.');
              localStorage.removeItem('currentWorkspaceId');
              localStorage.removeItem('currentWorkspace');
              workspaceId = null;
            }
            
            // Use token's workspaceId (it's authoritative)
            workspaceId = tokenWorkspaceId;
            // Update localStorage to match token
            if (localStorage.getItem('currentWorkspaceId') !== tokenWorkspaceId) {
              localStorage.setItem('currentWorkspaceId', tokenWorkspaceId);
            }
          } else if (tokenPayload.workspaceIds && Array.isArray(tokenPayload.workspaceIds) && tokenPayload.workspaceIds.length > 0) {
            // Multiple workspaces - validate localStorage workspace is in the list
            if (workspaceId && !tokenPayload.workspaceIds.includes(workspaceId)) {
              // Workspace in localStorage is not in user's workspaces - clear it
              console.warn('Workspace in localStorage is not in user\'s workspaces. Clearing it.');
              localStorage.removeItem('currentWorkspaceId');
              localStorage.removeItem('currentWorkspace');
              workspaceId = null;
            }
            // If no workspace selected yet, don't auto-select (user needs to choose)
            // Only use first workspace if localStorage already has it and it's valid
          } else if (tokenPayload.isPortalAdmin) {
            // Portal admin - clear workspace
            if (workspaceId) {
              localStorage.removeItem('currentWorkspaceId');
              localStorage.removeItem('currentWorkspace');
              workspaceId = null;
            }
          }
        } catch (e) {
          // JWT parsing failed, ignore
        }
      }
      
      // Add workspace header if available (unless bypassing workspace)
      if (workspaceId && !options.bypassWorkspace) {
        (headers as Record<string, string>)['X-Workspace-Id'] = workspaceId;
      } else if (!options.bypassWorkspace) {
        // Log warning if workspace ID is missing for authenticated requests
        console.warn('Workspace ID not found. Request may fail if workspace is required.');
      }
    }
  }

  const response = await fetch(input, { ...init, headers });

  let data: any = null;
  try {
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        // Not JSON, use text as error message
        data = { error: text };
      }
    }
  } catch {
    // Failed to read response
    data = null;
  }

  if (!response.ok) {
    let errorText: string;
    
    // Safely extract error message from response
    if (data && typeof data === 'object' && data.error) {
      errorText = typeof data.error === 'string' ? data.error : String(data.error);
    } else if (typeof data === 'string') {
      errorText = data;
    } else {
      errorText = `Request failed: ${response.status} ${response.statusText}`;
    }
    
    // Create error object with additional context
    const error = new Error(errorText);
    (error as any).status = response.status;
    (error as any).data = data;
    
    if (setMessage) {
      const msg: AppMessage = { type: 'error', text: errorText };
      setMessage(msg);
    }
    throw error;
  }
  
  // Ensure we return an array if the response is expected to be an array but is null/undefined
  if (data === null || data === undefined) {
    return [] as T;
  }

  if (successMessage && setMessage) {
    showTempMessage(setMessage, { type: 'success', text: successMessage }, successTimeoutMs);
  }

  return data as T;
}

